import React, {useEffect, useRef, useState} from 'react';
import {Image, Linking, View} from 'react-native';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import Box from '@/Atoms/Box';
import CCButton from '@/Atoms/CCButton';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {FontSizes, vs} from '@/Utils/Dimensions';
import SlideModal from '@/Atoms/SlideModal';
import CCTextInput from '@/Atoms/CCTextInput';
import CCFlatlist from '@/Atoms/CCFlatlist';
import CCRadio from '@/Atoms/CCRadio/CCRadio';
import Spacer from '@/Atoms/Spacer';
import useFetchTournamentEligibility from '@/Hooks/GetTournaments/useFetchTournamentEligibility';
import useFetchTournamentQuestions from '@/Hooks/GetTournaments/useFetchTournamentQuestions';
import useSaveTournamentQuestionsResponse from '@/Hooks/GetTournaments/useSaveTournamentQuestionsResponse';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import CCLoader from '@/Atoms/CCLoader';
import useGeneratePaymentApi from '@/Hooks/SubscriptionApi/useGeneratePayment';
import {useOrderStatusApi} from '@/Hooks/BooksApi/useBooksApi';
import useJoinTournament from '@/Hooks/TournamentsApi/useJoinTournament';

export type PaidTournamentRegistrationModalProps = {
  visible: boolean;
  tournament: any | null;
  userId: string;
  onClose: () => void;
  onRefreshDetails: () => void;
  /** When user returns from payment gateway with order_id in route */
  orderId?: string | null;
};

const PaidTournamentRegistrationModal: React.FC<
  PaidTournamentRegistrationModalProps
> = ({visible, tournament, userId, onClose, onRefreshDetails, orderId}) => {
  const userData = useLoggedInUserStore(state => state.userData);
  const [stage, setStage] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedTournament, setSelectedTournament] = useState<any | null>(
    null,
  );
  const registrationInitDoneRef = useRef(false);

  const handleOnSuccessGeneratePayment = (paymentUrlData: any) => {
    if (paymentUrlData?.data?.content?.payment_links?.web) {
      Linking.openURL(paymentUrlData?.data?.content?.payment_links?.web);
    } else if (paymentUrlData?.data?.success) {
      onClose();
      setStage(0);
      onRefreshDetails();
    }
  };

  const handleOnErrorGeneratePayment = (error: any) => {
    console.log('Error generating payment:', error);
  };

  const {mutate: generatePayment} = useGeneratePaymentApi(
    handleOnSuccessGeneratePayment,
    handleOnErrorGeneratePayment,
  );

  const {
    mutate: checkEligibility,
    data: eligibilityData,
    isLoading: eligibilityLoading,
  } = useFetchTournamentEligibility();

  const {
    mutate: getQuestions,
    data: questionsData,
    isLoading: questionLoading,
  } = useFetchTournamentQuestions();

  const {mutate: joinTournamentRest} = useJoinTournament(() => {
    onRefreshDetails();
  });

  const {
    mutate: orderBookStatus,
    data: orderStatusResponse,
    isLoading: isStatusLoading,
  } = useOrderStatusApi();

  /* eslint-disable react-hooks/exhaustive-deps -- mirrors legacy list modal; widening deps causes duplicate API calls */
  useEffect(() => {
    if (!eligibilityData) {
      return;
    }
    const payload = {
      tournament_id: String(selectedTournament?.registration_tid),
      user_id: Number(userId),
    };
    if (eligibilityData?.status) {
      getQuestions(payload);
      setStage(1);
    } else if (eligibilityData?.club_id) {
      setErrorMessage(eligibilityData?.error);
      payload.tournament_id = eligibilityData?.club_id;
      if (selectedTournament?.club) {
        setSelectedTournament({
          ...selectedTournament.club,
          registration_tid: eligibilityData?.club_id,
        });
      }
      checkEligibility({
        mobile_number: userData?.mobile_number,
        player_id: userData?.player,
        registration_tid: Number(payload.tournament_id),
      });
    }
  }, [eligibilityData]);

  useEffect(() => {
    if (questionsData?.questions && !questionsData?.questions?.length) {
      setStage(2);
    }
  }, [questionsData]);

  useEffect(() => {
    if (
      orderStatusResponse?.status === 'CHARGED' &&
      selectedTournament?.parent_id
    ) {
      joinTournamentRest({
        tournamentId: Number(selectedTournament?.parent_id),
        playerId: Number(userData?.player),
      });
    } else if (orderStatusResponse?.status === 'CHARGED') {
      joinTournamentRest({
        tournamentId: Number(selectedTournament?.id),
        playerId: Number(userData?.player),
      });
    }
  }, [orderStatusResponse, selectedTournament]);

  useEffect(() => {
    if (!orderId || !tournament) {
      return;
    }
    setSelectedTournament(tournament);
    setStage(4);
    orderBookStatus({order_id: orderId});
  }, [orderId, tournament?.id]);

  useEffect(() => {
    if (!visible) {
      registrationInitDoneRef.current = false;
      return;
    }
    if (!tournament || orderId) {
      return;
    }
    if (registrationInitDoneRef.current) {
      return;
    }
    registrationInitDoneRef.current = true;
    setErrorMessage('');
    setSelectedTournament(tournament);
    if (tournament.is_series) {
      setStage(3);
    } else {
      setStage(0);
      checkEligibility({
        mobile_number: userData?.mobile_number,
        player_id: userData?.player,
        registration_tid: tournament?.registration_tid,
      });
    }
  }, [visible, tournament?.id, orderId, tournament]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handlePayment = (category: string, amount: number) => {
    const payload: any = {
      category_str: category,
      customer_id: userData?.user_id,
      email: userData?.email,
      mobile_number: userData?.mobile_number,
      player_id: userData?.player,
      tournament_id: selectedTournament.cct_id,
      amount,
      return_url:
        typeof window !== 'undefined' && window.location?.href
          ? window.location.href
          : '',
    };
    if (selectedTournament?.parent_id && selectedTournament?.is_series) {
      payload.child_tournament_id = Number(selectedTournament.id);
    }
    generatePayment(payload);
  };

  const handleClose = () => {
    setStage(0);
    setErrorMessage('');
    onClose();
  };

  const modalTitle =
    stage === 4
      ? 'Payment Status'
      : stage === 3
      ? 'Series Registration'
      : stage === 1
      ? 'Answer Questions'
      : 'Select Entry Fee';

  return (
    <SlideModal
      type="side"
      visible={visible}
      modalHeaderTitle={modalTitle}
      onClose={handleClose}>
      <View style={{padding: 30}}>
        {(eligibilityLoading || questionLoading || isStatusLoading) && (
          <CCLoader loading />
        )}
        {stage === 4 ? (
          !isStatusLoading && (
            <PaymentStatusModal
              status={orderStatusResponse?.status}
              onClose={handleClose}
            />
          )
        ) : stage === 3 ? (
          <SeriesRegistrationModal tournament={selectedTournament} />
        ) : stage === 2 ? (
          <EntryFeeSelect
            categories={eligibilityData?.category_fees || {}}
            errorMessage={errorMessage}
            handlePayment={handlePayment}
          />
        ) : (
          <QuestionList
            questions={questionsData?.questions}
            userData={userData}
            selectedTournament={selectedTournament}
            setStage={setStage}
          />
        )}
      </View>
    </SlideModal>
  );
};

const QuestionList = ({
  questions = [],
  selectedTournament,
  userData,
  setStage,
}: {
  questions?: any[];
  selectedTournament: any;
  userData: any;
  setStage: (n: number) => void;
}) => {
  const [responses, setResponses] = useState<any>({});

  const {mutate: submitQuestions, data: submitResponse} =
    useSaveTournamentQuestionsResponse();

  useEffect(() => {
    if (submitResponse) {
      setStage(2);
    }
  }, [submitResponse, setStage]);

  const handleChange = (index: number, value: string) => {
    setResponses((prev: any) => ({
      ...prev,
      [index]: value,
    }));
  };

  return (
    <Box>
      <CCFlatlist
        data={questions}
        renderItem={({item}) => (
          <Box>
            <CCText>{item?.question_text}</CCText>
            <CCTextInput onChangeText={text => handleChange(item.id, text)} />
          </Box>
        )}
        keyExtractor={(item, index) => index.toString()}
      />
      <Spacer spacing={vs(20)} />
      <CCButton
        type="fill"
        disabled={
          Object.keys(responses).length !==
          questions?.filter(t => t.is_mandatory).length
        }
        onPress={() => {
          submitQuestions({
            tournament_id: selectedTournament?.registration_tid,
            player_id: userData?.player,
            responses: Object.entries(responses).map(
              ([question_id, response]) => ({
                question_id: Number(question_id),
                response: response as string,
              }),
            ),
            sender: userData?.mobile_number,
          });
        }}>
        Proceed
      </CCButton>
    </Box>
  );
};

const EntryFeeSelect = ({
  errorMessage,
  categories,
  handlePayment,
}: {
  errorMessage: string;
  categories: Record<string, number>;
  handlePayment: (category: string, amount: number) => void;
}) => {
  const [selectedCategory, setSelectedCategory] = useState<any>(null);

  return (
    <View>
      <Spacer spacing={vs(10)} />
      <CCText color={CCColors.Primary.Aqua} fontSize={FontSizes[16]}>
        {errorMessage}
      </CCText>
      <Spacer spacing={vs(30)} />
      <CCFlatlist
        data={Object.keys(categories || {}).map(key => ({
          label: `INR ${categories[key]} / ${key}`,
          value: categories[key],
        }))}
        renderItem={({item}) => (
          <CCRadio
            label={item?.label}
            value={item?.value}
            selected={selectedCategory?.label === item.label}
            onToggle={() => {
              setSelectedCategory(item);
            }}
            unselectedColor={CCColors.TextColor.Dark}
            style={{marginBottom: vs(10)}}
          />
        )}
        keyExtractor={(item, index) => index.toString()}
      />
      <Spacer spacing={vs(20)} />
      <CCButton
        type="fill"
        onPress={() =>
          handlePayment(selectedCategory?.label, selectedCategory?.value)
        }>
        Proceed to Payment
      </CCButton>
    </View>
  );
};

const SeriesRegistrationModal = ({tournament}: {tournament: any}) => {
  return (
    <View style={{alignItems: 'center', justifyContent: 'center'}}>
      <Spacer spacing={vs(20)} />
      <Box alignItems="center" justifyContent="center">
        <Image
          source={require('@/Assets/Images/Svgs/Tournaments.svg')}
          style={{width: 150, height: 150}}
          resizeMode="contain"
        />
      </Box>
      <Spacer spacing={vs(20)} />
      <CCText
        fontSize={20}
        fontWeight={600}
        style={{textAlign: 'center', lineHeight: 28}}>
        Register for Tournament Series
      </CCText>
      <Spacer spacing={vs(10)} />
      <CCText
        fontSize={16}
        color={CCColors.TextColor.LightGrey}
        style={{textAlign: 'center', lineHeight: 24}}>
        {`This tournament is part of the "${
          tournament?.parentName || tournament?.name
        }" series. You need to be registered in the series to play.`}
      </CCText>
      <CCButton
        type="fill"
        onPress={() =>
          Linking.openURL(
            'https://cc-tournaments.s3.ap-south-1.amazonaws.com/brochures/50108.pdf',
          )
        }>
        View Brochure
      </CCButton>
      <Spacer spacing={vs(30)} />

      <Box style={{width: '100%'}}>
        <Spacer spacing={vs(15)} />
        <CCButton
          type="outline"
          onPress={() => {
            const redirectUrl = `https://circlechess.com/registration?id=${tournament.cct_id}`;
            Linking.openURL(redirectUrl);
          }}
          style={{borderColor: '#25D366'}}>
          <CCText color="#25D366" fontWeight={600}>
            Register on WhatsApp
          </CCText>
        </CCButton>
      </Box>
    </View>
  );
};

const PaymentStatusModal = ({
  status,
  onClose,
}: {
  status: string | undefined;
  onClose: () => void;
}) => {
  const isSuccess = status === 'CHARGED';
  return (
    <View style={{alignItems: 'center', justifyContent: 'center'}}>
      <Spacer spacing={vs(20)} />
      <Box alignItems="center" justifyContent="center">
        <MaterialCommunityIcons
          name={isSuccess ? 'check-circle' : 'close-circle'}
          size={56}
          color={isSuccess ? CCColors.Green : CCColors.Red}
        />
      </Box>
      <Spacer spacing={vs(30)} />
      <CCText
        fontSize={22}
        fontWeight={600}
        style={{textAlign: 'center', lineHeight: 30}}>
        {isSuccess ? 'Payment Successful!' : 'Payment Failed'}
      </CCText>
      <Spacer spacing={vs(15)} />
      <CCText
        fontSize={16}
        color={CCColors.TextColor.LightGrey}
        style={{textAlign: 'center', lineHeight: 24}}>
        {isSuccess
          ? 'Your registration has been confirmed. Good luck with your tournament!'
          : 'We could not process your payment at this moment. Please try again later or contact support if the issue persists.'}
      </CCText>
      <Spacer spacing={vs(40)} />
      <CCButton type="fill" onPress={onClose} style={{width: '100%'}}>
        {isSuccess ? 'Great!' : 'Close'}
      </CCButton>
    </View>
  );
};

export default PaidTournamentRegistrationModal;
